import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
    fetchMedicalExpenseTypes,
    submitPatientVerification,
    MedicalExpenseType,
} from '~/services/patient-verification';
import { formatDateIfValid } from '~/utils';

interface Props {
    // An already-loaded patient (from PatientDetailSerializer).
    patient: any;
    // Called with the updated patient after a successful verification.
    onVerified?: (updatedPatient: any) => void;
    // Called when the user cancels (page navigates back / drawer closes).
    onCancel?: () => void;
    // Optional hook run just before the verification is submitted (e.g. the
    // drawer saving its medical-info edits). If it throws, verification is
    // aborted.
    onBeforeVerify?: () => Promise<void>;
}

// The medical-need verification form: uninsured-expense checklist, comments,
// and the verifier's name. Shared by the med-pro `VerifyClient` page and the
// CS-facing `PatientVerificationDrawer` so both submit through the same
// validation and `PATCH /admin/patients/<pk>/verify` path.
const PatientVerificationForm: React.FC<Props> = ({
    patient,
    onVerified,
    onCancel,
    onBeforeVerify,
}) => {
    const [expenseTypes, setExpenseTypes] = useState<MedicalExpenseType[]>([]);
    const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<number>>(
        () => new Set((patient?.medical_expenses ?? []).map((e: any) => e.id))
    );
    const [otherText, setOtherText] = useState(
        patient?.medical_expenses_other ?? ''
    );
    const [verifierName, setVerifierName] = useState(patient?.verifier_name ?? '');
    const [verificationDate, setVerificationDate] = useState(() =>
        format(new Date(), 'yyyy-MM-dd')
    );
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const types = await fetchMedicalExpenseTypes(patient?.id);
                if (!cancelled) setExpenseTypes(types);
            } catch (e) {
                console.error('Failed to load medical expense types:', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [patient?.id]);

    const isVerified = !!patient?.verification_completion_date;

    const toggleExpense = (id: number) => {
        setSelectedExpenseIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const onSave = async () => {
        if (!patient?.id) return;
        if (selectedExpenseIds.size === 0) {
            toast.error('Please check at least one uninsured expense.');
            return;
        }
        if (!verifierName.trim()) {
            toast.error('Please enter your name as the verifying professional.');
            return;
        }
        if (!verificationDate) {
            toast.error('Please enter the verification date.');
            return;
        }
        setSaving(true);
        try {
            // Persist any related edits (e.g. the drawer's medical-info fields)
            // before recording the verification.
            if (onBeforeVerify) {
                await onBeforeVerify();
            }
            const updated = await submitPatientVerification(patient.id, {
                medical_expenses: Array.from(selectedExpenseIds),
                medical_expenses_other: otherText,
                verifier_name: verifierName.trim(),
                verification_date: verificationDate,
            });
            toast.success('Verification submitted. Thank you!');
            onVerified?.(updated);
        } catch (e: any) {
            toast.error(e?.message ?? 'Failed to submit verification.');
        } finally {
            setSaving(false);
        }
    };

    const [columnA, columnB] = useMemo(() => {
        const half = Math.ceil(expenseTypes.length / 2);
        return [expenseTypes.slice(0, half), expenseTypes.slice(half)];
    }, [expenseTypes]);

    return (
        <>
            <h3 className='mb-4 text-[18px]/[22px] font-bold'>
                Uninsured Expenses
            </h3>
            {isVerified ? (
                <div className='text-[14px]/[20px]'>
                    {patient.medical_expenses?.length ? (
                        <ul className='ml-6 list-disc'>
                            {patient.medical_expenses.map((e: any) => (
                                <li key={e.id}>{e.name}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className='text-[#949599]'>No expenses recorded.</p>
                    )}
                    {patient.medical_expenses_other ? (
                        <p className='mt-3 text-[#949599]'>
                            {patient.medical_expenses_other}
                        </p>
                    ) : null}
                </div>
            ) : (
                <>
                    <div className='grid grid-cols-2 gap-3'>
                        <div>
                            {columnA.map((type) => (
                                <label
                                    key={type.id}
                                    className='mb-2 flex items-center gap-2 text-[14px]'
                                >
                                    <input
                                        type='checkbox'
                                        checked={selectedExpenseIds.has(type.id)}
                                        onChange={() => toggleExpense(type.id)}
                                    />
                                    {type.name}
                                </label>
                            ))}
                        </div>
                        <div>
                            {columnB.map((type) => (
                                <label
                                    key={type.id}
                                    className='mb-2 flex items-center gap-2 text-[14px]'
                                >
                                    <input
                                        type='checkbox'
                                        checked={selectedExpenseIds.has(type.id)}
                                        onChange={() => toggleExpense(type.id)}
                                    />
                                    {type.name}
                                </label>
                            ))}
                        </div>
                    </div>
                    <textarea
                        className='mt-4 w-full rounded border border-stroke px-3 py-2 text-[14px]'
                        rows={4}
                        placeholder='Additional comments can be entered here...'
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                    />
                </>
            )}

            <hr className='my-6' />

            {isVerified ? (
                <>
                    <div className='mb-2'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            Name of Medical Professional verifying expenses
                        </label>
                        <div className='text-[14px]'>
                            {patient.verifier_name ?? '—'}
                        </div>
                    </div>
                    <div className='mb-2 text-[12px]/[16px] text-[#949599]'>
                        Verified on{' '}
                        {formatDateIfValid(
                            patient.verification_completion_date,
                            'MM/dd/yy'
                        )}
                    </div>
                    <p className='mt-6 text-[20px]/[24px] font-bold text-primary'>
                        Verification Complete. Thank you!
                    </p>
                </>
            ) : (
                <>
                    <p className='mb-3 text-[14px]/[20px]'>
                        By saving, I am verifying that the medical information of
                        the applicant is correct to the best of my knowledge.
                    </p>
                    <div className='mb-4 flex gap-4'>
                        <div className='flex-1'>
                            <label className='mb-1 block text-[12px]/[16px] font-extrabold'>
                                Name of Medical Professional verifying expenses
                                <span className='ml-1 text-[10px] text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            <input
                                type='text'
                                className='w-full rounded border border-stroke px-3 py-2 text-[14px]'
                                value={verifierName}
                                onChange={(e) => setVerifierName(e.target.value)}
                            />
                        </div>
                        <div className='w-[40%]'>
                            <label className='mb-1 block text-[12px]/[16px] font-extrabold'>
                                Date
                                <span className='ml-1 text-[10px] text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            <input
                                type='date'
                                className='w-full rounded border border-stroke px-3 py-2 text-[14px]'
                                value={verificationDate}
                                onChange={(e) =>
                                    setVerificationDate(e.target.value)
                                }
                            />
                        </div>
                    </div>
                    <div className='flex justify-end gap-3'>
                        <button
                            type='button'
                            onClick={() => onCancel?.()}
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            onClick={onSave}
                            disabled={saving}
                            className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] disabled:opacity-50'
                        >
                            {saving ? 'Verifying…' : 'Verify'}
                        </button>
                    </div>
                </>
            )}
        </>
    );
};

export default PatientVerificationForm;
